import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  DialogueResult,
  MessageResult
} from "../../api/generated/data-contracts";

interface PrivateChatState {
  dialogues: DialogueResult[];
  isLoading: boolean;
  openedDialogue: {
    dialogue: DialogueResult | null;
    messages: MessageResult[];
  } | null;
}

const initialState: PrivateChatState = {
  dialogues: [],
  isLoading: false,
  openedDialogue: null
};

const privateChatSlice = createSlice({
  name: "privateChat",
  initialState,
  reducers: {
    addMessageToDialogue(state, action: PayloadAction<DialogueResult>) {
      const { lastMessage, interlocutorAuth0Id, userAuth0Id } = action.payload;

      let dialogue = state.dialogues.find(
        (d) =>
          d.userAuth0Id === userAuth0Id &&
          d.interlocutorAuth0Id === interlocutorAuth0Id
      );

      if (dialogue) {
        dialogue.lastMessage = lastMessage;
      } else {
        state.dialogues.push(action.payload);
        dialogue = action.payload;
      }

      if (
        state.openedDialogue &&
        state.openedDialogue.dialogue?.userAuth0Id === userAuth0Id &&
        state.openedDialogue.dialogue?.interlocutorAuth0Id ===
          interlocutorAuth0Id
      ) {
        const existingIndex = state.openedDialogue.messages.findIndex(
          (msg) => msg.uuid === lastMessage.uuid
        );

        if (existingIndex !== -1) {
          state.openedDialogue.messages[existingIndex] = lastMessage;
        } else {
          state.openedDialogue.messages.push(lastMessage);
        }
      }
    },
    removeMessageFromDialogue(
      state,
      action: PayloadAction<{
        messageId: number;
      }>
    ) {
      const { messageId } = action.payload;
      if (state.openedDialogue) {
        state.openedDialogue.messages = state.openedDialogue.messages.filter(
          (message) => message.id !== messageId
        );
      }
    },
    setDialogueOpened(
      state,
      action: PayloadAction<{
        userId: string;
        interlocutorId: string;
        messages: MessageResult[] | null | undefined;
      }>
    ) {
      const { userId, interlocutorId, messages } = action.payload;

      const dialogue = state.dialogues.find(
        (d) =>
          d.userAuth0Id === userId && d.interlocutorAuth0Id === interlocutorId
      );

      if (dialogue) {
        state.openedDialogue = {
          dialogue,
          messages: messages ?? []
        };
      }
    },
    setPrivateChatLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setDialogues(state, action: PayloadAction<DialogueResult[]>) {
      state.dialogues = action.payload;
    }
  }
});

export const {
  addMessageToDialogue: updateDialogue,
  removeMessageFromDialogue,
  setDialogueOpened,
  setPrivateChatLoading,
  setDialogues
} = privateChatSlice.actions;

export const privateChatReducer = privateChatSlice.reducer;
